import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { HardDrive, Search, Plus, FolderOpen, Tag, X, Download } from "lucide-react";
import { useAssets, useEntities, useScannerScripts } from "../../hooks/useAssets";
import { useAuth } from "../../context/AuthContext";
import { Button, Spinner, Badge } from "../../components/ui/index.jsx";
import { DATA_ISSUE_LABELS } from "../../utils/constants";
import { StatusBadge } from "../../components/asset/AssetHelpers.jsx";
import apiClient from "../../api/client";

const CAN_CREATE = ["ADMIN", "GA"];

// ---------------------------------------------------------------------------
// Tag autocomplete input
// ---------------------------------------------------------------------------
function TagFilterInput({ value, onChange }) {
  const [input, setInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef();
  const wrapperRef = useRef();

  useEffect(() => {
    function handleClick(e) {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      apiClient
        .get("/api/storage/contents/tags", {
          params: { q: input.toLowerCase() },
        })
        .then((r) => setSuggestions(r.data))
        .catch(() => {});
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [input]);

  function select(tag) {
    onChange(tag);
    setInput("");
    setOpen(false);
  }

  if (value) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-primary-300 bg-primary-50 px-3 py-2">
        <Tag className="h-4 w-4 text-primary-500 shrink-0" />
        <span className="text-sm text-primary-700 font-medium">#{value}</span>
        <button
          type="button"
          onClick={() => onChange("")}
          className="ml-1 text-primary-400 hover:text-primary-700 transition-colors"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="relative">
        <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          type="text"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Filter by tag..."
          className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden max-h-48 overflow-y-auto">
          {suggestions.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => select(t)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"
            >
              <Tag className="h-3 w-3 text-slate-400" />#{t}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tag search results — shows storage content entries grouped by storage unit
// ---------------------------------------------------------------------------
function TagSearchResults({ tag, searchQuery }) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const openAsset = (id) =>
    navigate(`/assets/${id}`, {
      state: { from: location.pathname + location.search },
    });

  useEffect(() => {
    if (!tag && !searchQuery) return;
    setLoading(true);
    apiClient
      .get("/api/storage/contents/search", {
        params: {
          ...(tag && { tag }),
          ...(searchQuery && { q: searchQuery }),
          limit: 50,
        },
      })
      .then((r) => setResults(r.data))
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [tag, searchQuery]);

  if (loading)
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );

  if (results.length === 0)
    return (
      <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
        <FolderOpen className="h-10 w-10 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500 text-sm">
          No content found{tag ? ` tagged #${tag}` : ""}.
        </p>
      </div>
    );

  // Group by storage unit
  const grouped = results.reduce((acc, item) => {
    const key = item.storageAssetId;
    if (!acc[key]) acc[key] = { storage: item.storage, items: [] };
    acc[key].items.push(item);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      {Object.values(grouped).map(({ storage, items }) => (
        <div
          key={storage.id}
          className="bg-white border border-slate-200 rounded-xl overflow-hidden"
        >
          {/* Storage unit header */}
          <button
            type="button"
            onClick={() => openAsset(storage.id)}
            className="w-full flex items-center gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors border-b border-slate-200 text-left"
          >
            <HardDrive className="h-4 w-4 text-slate-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 truncate">
                {storage.name}
              </p>
              <p className="text-xs text-slate-400 font-mono">
                {storage.assetNumber}
              </p>
            </div>
            {storage.storageCapacity && (
              <span className="text-xs text-slate-500 shrink-0">
                {storage.storageCapacity}
              </span>
            )}
            {storage.entity && (
              <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded shrink-0">
                {storage.entity.code}
              </span>
            )}
          </button>

          {/* Content items */}
          <div className="divide-y divide-slate-100">
            {items.map((item) => (
              <div key={item.id} className="flex items-start gap-3 px-4 py-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800">
                    {item.name}
                  </p>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-0.5">
                    {item.projectName && (
                      <span className="text-xs text-slate-500">
                        {item.projectName}
                      </span>
                    )}
                    {item.sizeGB && (
                      <span className="text-xs text-slate-400">
                        {parseFloat(item.sizeGB).toFixed(1)} GB
                      </span>
                    )}
                  </div>
                  {item.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {item.tags.map((t) => (
                        <span
                          key={t}
                          className={`text-xs px-2 py-0.5 rounded-full ${t === tag ? "bg-primary-600 text-white" : "bg-slate-100 text-slate-500"}`}
                        >
                          #{t}
                        </span>
                      ))}
                    </div>
                  )}
                  {item.notes && (
                    <p className="text-xs text-slate-400 mt-1 truncate">
                      {item.notes}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Storage unit card grid
// ---------------------------------------------------------------------------
function StorageCard({ asset, onClick }) {
  return (
    <button
      onClick={onClick}
      className="bg-white border border-slate-200 rounded-xl p-5 text-left hover:border-primary-300 hover:shadow-sm transition-all group"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center group-hover:bg-primary-50 transition-colors">
          <HardDrive className="h-5 w-5 text-slate-500 group-hover:text-primary-600 transition-colors" />
        </div>
        <StatusBadge status={asset.status} />
      </div>
      <p className="font-semibold text-slate-900 truncate">{asset.name}</p>
      <p className="font-mono text-xs text-slate-400 mt-0.5">
        {asset.assetNumber}
      </p>
      {asset.dataIssues?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {asset.dataIssues.map((i) => (
            <Badge key={i} variant="warning">
              {DATA_ISSUE_LABELS[i] ?? i}
            </Badge>
          ))}
        </div>
      )}
      <div className="mt-3 space-y-1.5">
        {asset.storageCapacity && (
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span className="text-slate-400 w-16 shrink-0">Capacity</span>
            <span className="font-medium">{asset.storageCapacity}</span>
          </div>
        )}
        {asset.storageType && (
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span className="text-slate-400 w-16 shrink-0">Type</span>
            <span className="font-medium">{asset.storageType}</span>
          </div>
        )}
        {asset.storageLocation && (
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span className="text-slate-400 w-16 shrink-0">Location</span>
            <span className="truncate">{asset.storageLocation}</span>
          </div>
        )}
        {asset.entity && (
          <div className="flex items-center gap-2 text-xs">
            <span className="text-slate-400 w-16 shrink-0">Entity</span>
            <span className="bg-slate-100 px-1.5 py-0.5 rounded text-xs font-medium text-slate-600">
              {asset.entity.code}
            </span>
          </div>
        )}
      </div>
      {asset.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t border-slate-100">
          {asset.tags.map((t) => (
            <span
              key={t}
              className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500"
            >
              #{t}
            </span>
          ))}
        </div>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Scanner script download — always the current version, zipped and synced
// to R2 on every backend deploy (see backend/scanner-scripts/). One zip
// (not separate file links) because scan-drive.bat needs scan-drive.ps1
// alongside it in the same folder.
// ---------------------------------------------------------------------------
function ScannerDownloadButton() {
  const { data } = useScannerScripts();
  return (
    <a href={data?.url} download>
      <Button variant="secondary" disabled={!data?.url}>
        <Download className="h-4 w-4" /> Download Scanner
      </Button>
    </a>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function StoragePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filters live in the URL (like the asset list) so the detail page's back
  // button can return to exactly this view, and a refresh or pasted link
  // restores it.
  const params = {
    page: Number(searchParams.get("page")) || 1,
    limit: 25,
    assetType: "STORAGE_UNIT",
    search: searchParams.get("search") || "",
    entityId: searchParams.get("entityId") || "",
  };
  const activeTag = searchParams.get("tag") || "";
  const [searchInput, setSearchInput] = useState(params.search);

  // Keep the search box in sync on back/forward navigation
  useEffect(() => {
    setSearchInput(params.search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("search")]);

  // Where the detail page's back button should return to
  const openAsset = (id) =>
    navigate(`/assets/${id}`, {
      state: { from: location.pathname + location.search },
    });

  // When tag or text search active → show tag search results instead of asset grid
  const isTagSearch = !!activeTag;

  const { data, isLoading } = useAssets(
    !isTagSearch
      ? Object.fromEntries(
          Object.entries({ ...params, assetType: "STORAGE_UNIT" }).filter(
            ([, v]) => v !== "",
          ),
        )
      : null, // skip when doing tag search
  );

  const { data: entities = [] } = useEntities();
  const assets = data?.data ?? [];
  const pagination = data?.pagination;

  function setFilter(key, value) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      next.delete("page"); // any filter change starts back at page 1
      return next;
    });
  }

  function goToPage(page) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", String(page));
      return next;
    });
  }

  function handleSearch(e) {
    e.preventDefault();
    setFilter("search", searchInput);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Storage Units</h1>
          {!isTagSearch && pagination && (
            <p className="text-sm text-slate-500 mt-0.5">
              {pagination.total} storage unit{pagination.total !== 1 ? "s" : ""}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ScannerDownloadButton />
          {CAN_CREATE.includes(user?.role) && (
            <Button onClick={() => navigate("/assets/new?type=STORAGE_UNIT")}>
              <Plus className="h-4 w-4" /> Add Storage Unit
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex flex-wrap gap-3">
        <form onSubmit={handleSearch} className="flex gap-2 flex-1 min-w-48">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder={
                isTagSearch
                  ? "Search content by name..."
                  : "Search storage units..."
              }
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <Button type="submit" variant="secondary" size="sm">
            Search
          </Button>
        </form>

        {/* Tag filter */}
        <div className="min-w-44">
          <TagFilterInput
            value={activeTag}
            onChange={(tag) => {
              setSearchInput("");
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                if (tag) next.set("tag", tag);
                else next.delete("tag");
                next.delete("search");
                next.delete("page");
                return next;
              });
            }}
          />
        </div>

        {!isTagSearch && (
          <select
            value={params.entityId}
            onChange={(e) => setFilter("entityId", e.target.value)}
            className="text-sm border border-slate-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Entities</option>
            {entities.map((e) => (
              <option key={e.id} value={e.id}>
                {e.code} — {e.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Tag search results */}
      {isTagSearch ? (
        <TagSearchResults tag={activeTag} searchQuery={searchInput} />
      ) : (
        <>
          {isLoading ? (
            <div className="flex justify-center py-16">
              <Spinner />
            </div>
          ) : assets.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
              <HardDrive className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">No storage units found.</p>
              {CAN_CREATE.includes(user?.role) && (
                <Button
                  className="mt-4"
                  size="sm"
                  onClick={() => navigate("/assets/new?type=STORAGE_UNIT")}
                >
                  Add your first storage unit
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {assets.map((asset) => (
                <StorageCard
                  key={asset.id}
                  asset={asset}
                  onClick={() => openAsset(asset.id)}
                />
              ))}
            </div>
          )}

          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-slate-500">
                Page {pagination.page} of {pagination.totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={pagination.page <= 1}
                  onClick={() => goToPage(pagination.page - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => goToPage(pagination.page + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
